import {
  Airplane01Icon,
  ArrowDataTransferHorizontalIcon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  BellIcon,
  BotIcon,
  BrainIcon,
  BubbleChatIcon,
  CreditCardIcon,
  GaugeIcon,
  File01Icon,
  Flag01Icon,
  GraduationCapIcon,
  HeartIcon,
  InformationCircleIcon,
  JobSearchIcon,
  Key01Icon,
  Link01Icon,
  Mail01Icon,
  MailSend01Icon,
  Linkedin02Icon,
  PlugSocketIcon,
  PreferenceVerticalIcon,
  SecurityCheckIcon,
  SparklesIcon,
  Stethoscope02Icon,
  Globe02Icon,
  GlobalSearchIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import type { CvPresetIconKey } from '../onboarding/cv-presets';
import type {
  SkillCatalogIconKey,
  SkillIconKey,
} from '../skills/skill-taxonomy';
import type { SettingsCategoryMeta } from '../settings/settings-categories';
import type { HugeIconData } from './HugeIcon';

export const SKILL_ICON_NAMES: Record<SkillIconKey, string> = {
  cv: 'File01Icon',
  letter: 'Mail01Icon',
  interview: 'GraduationCapIcon',
  social: 'SparklesIcon',
  outreach: 'MailSend01Icon',
  jobSearch: 'JobSearchIcon',
  coaching: 'GraduationCapIcon',
  wellbeing: 'HeartIcon',
  generic: 'SparklesIcon',
};

export const SKILL_ICONS: Record<SkillIconKey, HugeIconData> = {
  cv: File01Icon,
  letter: Mail01Icon,
  interview: GraduationCapIcon,
  social: SparklesIcon,
  outreach: MailSend01Icon,
  jobSearch: JobSearchIcon,
  coaching: GraduationCapIcon,
  wellbeing: HeartIcon,
  generic: SparklesIcon,
};

export const SKILL_CATALOG_ICONS: Record<SkillCatalogIconKey, HugeIconData> = {
  ...SKILL_ICONS,
  relocateToGulf: Airplane01Icon,
  linkedin: Linkedin02Icon,
  cvHealthCheck: Stethoscope02Icon,
  gulfExecutive: GlobalSearchIcon,
};

export const SKILL_CATALOG_ICON_NAMES: Record<SkillCatalogIconKey, string> = {
  ...SKILL_ICON_NAMES,
  relocateToGulf: 'Airplane01Icon',
  linkedin: 'Linkedin02Icon',
  cvHealthCheck: 'Stethoscope02Icon',
  gulfExecutive: 'GlobalSearchIcon',
};

/**
 * Onboarding CV-preset artwork. `flag-uae` is deliberately absent: it renders
 * the bundled UAE flag through `GulfFlagIcon`, not a Hugeicons glyph, exactly
 * like the skills catalog does for country rows.
 */
export type CvPresetGlyphKey = Exclude<CvPresetIconKey, 'flag-uae'>;

export const CV_PRESET_ICONS: Record<CvPresetGlyphKey, HugeIconData> = {
  graduation: GraduationCapIcon,
  people: UserGroupIcon,
  switch: ArrowDataTransferHorizontalIcon,
  globe: Globe02Icon,
};

export const CV_PRESET_ICON_NAMES: Record<CvPresetGlyphKey, string> = {
  graduation: 'GraduationCapIcon',
  people: 'UserGroupIcon',
  switch: 'ArrowDataTransferHorizontalIcon',
  globe: 'Globe02Icon',
};

/**
 * Settings rows drawn by a Hugeicons glyph. `uaePass` is deliberately absent: it
 * renders the official UAE PASS thumbprint through `SettingsCategoryIcon`, not a
 * Hugeicons glyph, exactly like the CV presets do for `flag-uae`.
 */
export type SettingsGlyphKey = Exclude<SettingsCategoryMeta['icon'], 'uaePass'>;

export const SETTINGS_ICON_NAMES: Record<SettingsGlyphKey, string> = {
  user: 'UserCircleIcon',
  creditCard: 'CreditCardIcon',
  usage: 'GaugeIcon',
  sliders: 'PreferenceVerticalIcon',
  bell: 'BellIcon',
  shieldCheck: 'SecurityCheckIcon',
  flag: 'Flag01Icon',
  keyRound: 'Key01Icon',
  link: 'Link01Icon',
  bot: 'BotIcon',
  brain: 'BrainIcon',
  messageSquare: 'BubbleChatIcon',
  plug: 'PlugSocketIcon',
  info: 'InformationCircleIcon',
};

export const SETTINGS_ICONS: Record<SettingsGlyphKey, HugeIconData> = {
  user: UserCircleIcon,
  creditCard: CreditCardIcon,
  usage: GaugeIcon,
  sliders: PreferenceVerticalIcon,
  bell: BellIcon,
  shieldCheck: SecurityCheckIcon,
  flag: Flag01Icon,
  keyRound: Key01Icon,
  link: Link01Icon,
  bot: BotIcon,
  brain: BrainIcon,
  messageSquare: BubbleChatIcon,
  plug: PlugSocketIcon,
  info: InformationCircleIcon,
};

export function directionalArrowIcon(
  isRtl: boolean,
  direction: 'back' | 'forward',
) {
  const pointsLeft = direction === 'back' ? !isRtl : isRtl;
  return pointsLeft ? ArrowLeft01Icon : ArrowRight01Icon;
}
